import { memo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

import type { BillingPaymentCard } from './billing.interface'
import { BillingCardBrandIcon } from './billing-card-brand-icon.atom'
import { BillingPaymentCardActions } from './billing-payment-card-actions.atom'

export interface BillingPaymentCardItemProps {
  card: BillingPaymentCard
  isOnlyCard?: boolean
  onSetDefault?: (card: BillingPaymentCard) => void
  onDelete?: (card: BillingPaymentCard) => void
  className?: string
}

const formatExpiry = (month: number, year: number): string => {
  const monthStr = month.toString().padStart(2, '0')
  const yearStr = year.toString().slice(-2)
  return `${monthStr}/${yearStr}`
}

const BillingPaymentCardItemBase: React.FC<BillingPaymentCardItemProps> = ({
  card,
  isOnlyCard = false,
  onSetDefault,
  onDelete,
  className,
}): ReactNode => {
  return (
    <div
      className={cn(
        'billing-payment-card-item flex items-center gap-4 rounded-lg border bg-card p-4',
        className
      )}
    >
      <BillingCardBrandIcon brand={card.brand} size="lg" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium capitalize">{card.brand}</span>
          <span className="text-muted-foreground">****</span>
          <span className="font-mono">{card.last4}</span>
          {card.isDefault && (
            <Badge variant="secondary" className="text-xs">
              Default
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Expires {formatExpiry(card.expMonth, card.expYear)}
        </p>
      </div>
      <BillingPaymentCardActions
        card={card}
        isOnlyCard={isOnlyCard}
        onSetDefault={onSetDefault}
        onDelete={onDelete}
      />
    </div>
  )
}

export const BillingPaymentCardItem = memo(BillingPaymentCardItemBase)

BillingPaymentCardItem.displayName = 'BillingPaymentCardItem'
